import {Component, OnInit, ViewChild} from '@angular/core';
import {BreadcrumbService} from 'xng-breadcrumb';
import {Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TokenStorageService} from '../../_services/token-storage.service';
import {FileUploadService} from '../../add-item/file-upload.service';
import {
  AttributeDefinitionView,
  AttributeSchemaService,
  ListingAttributeValueView
} from '../../shared/attribute-schema.service';
import {AttributeFormComponent} from '../../shared/attribute-form/attribute-form.component';
import {AppConfigService} from '../../shared/app-config.service';
import {ProfileFlashService} from '../../profile/profile-flash.service';
import {buildUpdateListingRouterLink, taxonomySegmentsFromBc} from '../../shared/item-url.utils';

/** Category-agnostic edit-listing form (aligned with add-listing details UI). */
@Component({
  standalone: false,
  selector: 'app-update-listing-form',
  templateUrl: './update-listing-form.component.html',
  styleUrls: ['../../shared/listing-form-styles/listing-form-layout.css', '../../shared/listing-form-styles/listing-form-checkbox.scss']
})
export class UpdateListingFormComponent implements OnInit {
  url: any;
  urls: string[] = [];
  itemId = '';
  countiesArray: string[] = [];
  listingDetailsForm: FormGroup;
  attributeSchema: AttributeDefinitionView[] = [];
  attributeValues: Record<string, unknown> = {};
  attributeInitialValues: Record<string, unknown> = {};
  attributesEnabled = true;
  schemaError: string | null = null;
  loadError: string | null = null;
  saveError: string | null = null;
  saveMessage: string | null = null;
  saving = false;
  currentUser: any;
  listingStatus = '';
  statusReason = '';
  categoryPath = '';

  @ViewChild(AttributeFormComponent) attributeForm?: AttributeFormComponent;

  constructor(private token: TokenStorageService,
              private router: Router,
              private uploadService: FileUploadService,
              private attributeSchemaService: AttributeSchemaService,
              private appConfigService: AppConfigService,
              private profileFlash: ProfileFlashService,
              private breadcrumbService: BreadcrumbService,
              private formBuilder: FormBuilder) {
    environment.then(env => {
      this.url = env['backendURL'];
    });
  }

  ngOnInit(): void {
    this.urls = this.router.url.split('/').filter(Boolean);
    this.itemId = this.urls[this.urls.length - 1] || '';
    this.currentUser = this.token.getUser();

    this.listingDetailsForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      value: ['', [Validators.required]],
      location: ['', [Validators.required]],
      description: ['', [Validators.required]]
    });

    this.breadcrumbService.set('@par', 'Edit listing');

    this.appConfigService.attributesEnabled().subscribe(enabled => {
      this.attributesEnabled = enabled;
    });
    this.loadListing();

    this.uploadService.getCounties(this.url + '/counties').subscribe(response => {
      const counties: string[] = response as string[];
      counties.forEach(name => {
        if (name && !this.countiesArray.includes(name)) {
          this.countiesArray.push(name);
        }
      });
    });
  }

  get isRejected(): boolean {
    return this.listingStatus === 'REJECTED';
  }

  get isDraft(): boolean {
    return this.listingStatus === 'DRAFT';
  }

  get canResubmit(): boolean {
    return this.isRejected || this.isDraft;
  }

  get statusBannerTitle(): string {
    switch (this.listingStatus) {
      case 'REJECTED':
        return 'This listing was rejected';
      case 'PENDING':
        return 'This listing is awaiting review';
      case 'DRAFT':
        return 'This listing is still a draft';
      case 'PUBLISHED':
        return 'This listing is live';
      default:
        return this.listingStatus ? `Status: ${this.listingStatus}` : '';
    }
  }

  get statusBannerClass(): string {
    switch (this.listingStatus) {
      case 'REJECTED':
        return 'status-banner status-banner-rejected';
      case 'PENDING':
        return 'status-banner status-banner-pending';
      case 'DRAFT':
        return 'status-banner status-banner-draft';
      case 'PUBLISHED':
        return 'status-banner status-banner-live';
      default:
        return 'status-banner';
    }
  }

  private loadListing(): void {
    if (!this.itemId) {
      this.loadError = 'Missing listing id in URL.';
      return;
    }

    this.uploadService.getListingForEdit(this.url, this.itemId).subscribe({
      next: payload => {
        const item = payload?.item;
        if (!item) {
          this.loadError = 'Listing not found.';
          return;
        }

        this.canonicaliseUpdateUrl(item);

        this.breadcrumbService.set('@par', item.name);
        this.listingStatus = String(item.status || '').toUpperCase();
        this.statusReason = item.statusReason || '';
        const location = item.location || item.locationId || {};
        this.listingDetailsForm.patchValue({
          name: item.name,
          value: item.value,
          location: location.region || location.name || location.city || '',
          description: item.description
        });

        this.categoryPath = this.resolveCategoryPath(item);
        if (this.attributesEnabled) {
          this.loadAttributeSchema(this.categoryPath);
        }

        const attributes: ListingAttributeValueView[] = this.attributesEnabled
          ? (payload?.attributes || [])
          : [];
        this.attributeInitialValues = this.attributeSchemaService.attributesToFormValues(attributes);
        this.attributeValues = {...this.attributeInitialValues};
        this.loadError = null;
      },
      error: () => {
        this.loadError = 'Unable to load listing.';
      }
    });
  }

  private canonicaliseUpdateUrl(item: any): void {
    const taxonomy = taxonomySegmentsFromBc(item?.bc);
    if (!taxonomy.length || !item?.itemId) {
      return;
    }
    const expectedPath = `/update-item/${taxonomy.join('/')}/${item.itemId}`;
    const currentPath = (this.router.url || '').split('?')[0];
    if (currentPath !== expectedPath) {
      void this.router.navigate(buildUpdateListingRouterLink(item), {replaceUrl: true});
      this.urls = ['update-item', ...taxonomy, String(item.itemId)];
    }
  }

  private resolveCategoryPath(item: any): string {
    const fromBc = taxonomySegmentsFromBc(item?.bc);
    if (fromBc.length) {
      return fromBc.join('/');
    }
    return this.attributeSchemaService.categoryPathFromUrlSegments(this.urls);
  }

  private loadAttributeSchema(categoryPath: string): void {
    this.attributeSchemaService.getSchema(categoryPath || '*').subscribe({
      next: schema => {
        this.attributeSchema = schema?.attributes || [];
        this.schemaError = null;
      },
      error: () => {
        this.attributeSchema = [];
        this.schemaError = 'Unable to load category attribute schema.';
      }
    });
  }

  onAttributeValuesChange(values: Record<string, unknown>): void {
    this.attributeValues = values || {};
  }

  saveListing(resubmit = false): void {
    this.listingDetailsForm.markAllAsTouched();
    const hasAttributeFields = this.attributeForm?.sectionGroups?.length;
    const attributesValid = !this.attributesEnabled
      || !hasAttributeFields
      || (this.attributeForm?.validate() ?? true);
    if (this.listingDetailsForm.invalid || !attributesValid) {
      this.saveError = 'Please complete all required fields.';
      return;
    }

    this.saving = true;
    this.saveError = null;
    this.saveMessage = null;

    const taxonomy = this.categoryPath
      ? this.categoryPath.split('/')
      : taxonomySegmentsFromBc(null);
    const payload = {
      listingDetails: {
        name: this.listingDetailsForm.get('name')?.value,
        value: this.listingDetailsForm.get('value')?.value,
        location: this.listingDetailsForm.get('location')?.value,
        description: this.listingDetailsForm.get('description')?.value,
        url: ['update-item', ...taxonomy, this.itemId]
      },
      attributes: this.attributesEnabled
        ? (this.attributeForm?.collectValues() ?? this.attributeValues)
        : {},
      resubmit: resubmit && this.canResubmit,
      userId: this.currentUser?.username,
      timestamp: new Date().toISOString()
    };

    this.uploadService.updateItemListing(this.url, this.itemId, payload).subscribe({
      next: response => {
        this.saving = false;
        const status = String(response?.status || this.listingStatus).toUpperCase();
        this.listingStatus = status;
        if (resubmit && status === 'PENDING') {
          this.statusReason = '';
          this.profileFlash.setListingMessage('Changes saved and listing resubmitted for review.');
          void this.router.navigate(['/profile']);
          return;
        } else if (this.canResubmit) {
          this.saveMessage = 'Changes saved. Resubmit when you are ready for review.';
        } else {
          this.saveMessage = 'Changes saved.';
        }
      },
      error: err => {
        this.saving = false;
        this.saveError = err?.error?.error || 'Failed to save listing changes.';
      }
    });
  }
}
